import { OrganizationType } from '../models/Organization';
import { SubscriptionPlanCode, SubscriptionStatus } from '../models/Subscription';

export const SUBSCRIPTION_AWAITING_STRIPE_KEY = 'subscription_awaiting_stripe';
export const ORGANIZATION_TYPE_UPDATED_EVENT = 'bandspace:organization-type-updated';

/** How long to poll after returning from Stripe before giving up. */
export const STRIPE_RETURN_POLL_TIMEOUT_MS = 20_000;
export const STRIPE_RETURN_POLL_INTERVAL_MS = 2_000;

export type SubscriptionAwaitingStripeSnapshot = {
  startedAt: number;
  planCode: SubscriptionPlanCode | string | null;
  status: SubscriptionStatus | string | null;
  organizationType: OrganizationType | string | null;
};

const OPEN_ACTIVATED = new Set<string>([
  SubscriptionStatus.TRIALING,
  SubscriptionStatus.ACTIVE,
]);

export function markSubscriptionAwaitingStripe(
  snapshot: Omit<SubscriptionAwaitingStripeSnapshot, 'startedAt'>,
): void {
  const payload: SubscriptionAwaitingStripeSnapshot = {
    startedAt: Date.now(),
    ...snapshot,
  };
  sessionStorage.setItem(SUBSCRIPTION_AWAITING_STRIPE_KEY, JSON.stringify(payload));
}

export function readSubscriptionAwaitingStripe(): SubscriptionAwaitingStripeSnapshot | null {
  const raw = sessionStorage.getItem(SUBSCRIPTION_AWAITING_STRIPE_KEY);
  if (!raw) return null;
  try {
    const snapshot = JSON.parse(raw) as SubscriptionAwaitingStripeSnapshot;
    if (
      typeof snapshot.startedAt !== 'number' ||
      Date.now() - snapshot.startedAt > STRIPE_RETURN_POLL_TIMEOUT_MS
    ) {
      sessionStorage.removeItem(SUBSCRIPTION_AWAITING_STRIPE_KEY);
      return null;
    }
    return snapshot;
  } catch {
    sessionStorage.removeItem(SUBSCRIPTION_AWAITING_STRIPE_KEY);
    return null;
  }
}

export function clearSubscriptionAwaitingStripe(): void {
  sessionStorage.removeItem(SUBSCRIPTION_AWAITING_STRIPE_KEY);
}

/**
 * True when webhook-backed billing state (or org type) has moved past the
 * pre-Stripe snapshot, or when a first subscription reached TRIALING/ACTIVE.
 */
export function hasSubscriptionWebhookCaughtUp(
  baseline: SubscriptionAwaitingStripeSnapshot,
  current: {
    planCode: SubscriptionPlanCode | string | null;
    status: SubscriptionStatus | string | null;
    organizationType: OrganizationType | string | null | undefined;
  },
): boolean {
  const orgType = current.organizationType ?? null;
  if (orgType !== baseline.organizationType) {
    return true;
  }
  if (current.planCode !== baseline.planCode) {
    return true;
  }
  if (current.status !== baseline.status) {
    if (
      baseline.status == null &&
      current.status != null &&
      OPEN_ACTIVATED.has(String(current.status))
    ) {
      return true;
    }
    if (baseline.status != null && current.status !== baseline.status) {
      return true;
    }
  }
  return false;
}

export function notifyOrganizationTypeUpdated(type: OrganizationType): void {
  localStorage.setItem('organization_type', type);
  window.dispatchEvent(new CustomEvent(ORGANIZATION_TYPE_UPDATED_EVENT, { detail: type }));
}
